import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logoutFirebase } from '../../firebase/authService';
import { logout } from '../../store/slices/authSlice';
import { Outlet, NavLink } from 'react-router-dom';
import {
    LayoutDashboard,
    Users,
    CreditCard,
    LogOut,
    Contact,
    UserPlus,
    FileText,
    Menu,
    X
} from 'lucide-react';

const AdminLayout = () => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    const handleLogout = async () => {
        await logoutFirebase();
        dispatch(logout());
    };

    const closeSidebar = () => {
        setIsSidebarOpen(false);
    };

    return (
        <div className="flex h-screen bg-gray-50 overflow-hidden font-sans">
            {/* Overlay for mobile sidebar */}
            {isSidebarOpen && (
                <div 
                    className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm z-40 lg:hidden transition-opacity"
                    onClick={closeSidebar}
                />
            )}

            {/* Sidebar */}
            <aside className={`
                fixed lg:static inset-y-0 left-0 z-50
                w-72 lg:w-64 bg-white border-r border-gray-100 
                flex flex-col shadow-2xl lg:shadow-none
                transform transition-transform duration-300 cubic-bezier(0.4, 0, 0.2, 1)
                ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            `}>
                <div className="p-6 flex justify-between items-center shrink-0">
                    <h1 className="text-3xl font-black text-blue-600 tracking-tighter">Jardín<span className="text-gray-800">App</span></h1>
                    <button 
                        onClick={closeSidebar}
                        className="p-2 -mr-2 lg:hidden text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
                    >
                        <X size={24} />
                    </button>
                </div>

                <nav className="flex-1 px-4 py-2 space-y-1.5 overflow-y-auto w-full styled-scrollbar">
                    <NavItem to="/admin" icon={<LayoutDashboard size={20} />} label="Dashboard" onClick={closeSidebar} />
                    <NavItem to="/admin/estudiantes" icon={<Users size={20} />} label="Estudiantes" onClick={closeSidebar} />
                    <NavItem to="/admin/acudientes" icon={<Contact size={20} />} label="Acudientes" onClick={closeSidebar} />
                    <NavItem to="/admin/pagos" icon={<CreditCard size={20} />} label="Pagos y Facturas" onClick={closeSidebar} />
                    <NavItem to="/admin/generar-pdf" icon={<FileText size={20} />} label="Generar PDF" onClick={closeSidebar} />
                    <NavItem to="/admin/registrar-admin" icon={<UserPlus size={20} />} label="Administradores" onClick={closeSidebar} />
                </nav>

                <div className="p-4 border-t border-gray-100 bg-gray-50/50 shrink-0">
                    <div className="flex items-center gap-3 mb-4 bg-white p-3 rounded-2xl shadow-sm border border-gray-100">
                        {user?.photoURL ? (
                            <img
                                src={user.photoURL}
                                alt="Admin"
                                className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm shrink-0"
                                referrerPolicy="no-referrer"
                            />
                        ) : (
                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold shrink-0">
                                {user?.displayName?.charAt(0)?.toUpperCase() || 'A'}
                            </div>
                        )}
                        <div className="overflow-hidden">
                            <p className="text-sm font-bold text-gray-800 truncate">{user?.displayName || 'Administrador'}</p>
                            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                        </div>
                    </div>
                    <button
                        onClick={handleLogout}
                        className="flex items-center justify-center gap-2 w-full p-3 text-red-600 bg-red-50 hover:bg-red-100 hover:text-red-700 rounded-xl transition-colors text-sm font-bold shadow-sm"
                    >
                        <LogOut size={18} />
                        Cerrar Sesión
                    </button>
                </div>
            </aside>

            {/* Main Content Area */}
            <main className="flex-1 flex flex-col min-w-0 overflow-hidden h-screen bg-[#f8fafc]">
                <header className="h-16 sm:h-20 bg-white/80 backdrop-blur-md border-b border-gray-100 flex items-center justify-between px-4 sm:px-8 z-10 shrink-0 sticky top-0 shadow-sm">
                    <div className="flex items-center gap-3">
                        <button 
                            onClick={() => setIsSidebarOpen(true)}
                            className="p-2 -ml-2 text-gray-600 hover:bg-gray-100 rounded-xl lg:hidden focus:ring-2 focus:ring-blue-100 outline-none transition-colors"
                        >
                            <Menu size={24} />
                        </button>
                        <h2 className="text-lg sm:text-xl font-bold text-gray-800">Panel de Control</h2>
                    </div>
                    <div className="flex items-center gap-4">
                        <span className="hidden sm:inline-block text-xs font-black bg-emerald-50 text-emerald-600 px-4 py-1.5 rounded-full uppercase tracking-widest border border-emerald-100 shadow-sm">
                            {user?.role === 'dev' ? 'Dev' : 'Administrador'}
                        </span>
                        {/* Mobile Avatar (Solo visual, también abre el sidebar) */}
                        {user?.photoURL && (
                            <img
                                src={user.photoURL}
                                alt="Admin"
                                className="w-9 h-9 rounded-full lg:hidden border-2 border-white shadow-sm cursor-pointer"
                                referrerPolicy="no-referrer"
                                onClick={() => setIsSidebarOpen(true)}
                            />
                        )}
                    </div>
                </header>

                <section className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 w-full relative">
                    <Outlet />
                </section>
            </main>
        </div>
    );
};

// NavItem 
const NavItem = ({ icon, label, to, onClick }) => (
    <NavLink
        to={to}
        end={to === "/admin"} 
        onClick={onClick}
        className={({ isActive }) => `
            flex items-center gap-3 px-4 py-3.5 rounded-2xl transition-all duration-300 font-bold group outline-none
            ${isActive
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-200/50 scale-[1.02]'
                : 'text-gray-500 hover:bg-blue-50 hover:text-blue-600 focus:bg-blue-50 focus:text-blue-600'}
        `}
    >
        <div className="transition-transform duration-300 group-hover:scale-110">
            {icon}
        </div>
        <span className="text-[15px]">{label}</span>
    </NavLink>
);

export default AdminLayout;